import { useState } from 'react';
import logo from '../../../assets/new-logo.png';
import type { DashboardStatus } from '../../../services/strategyService';

interface SidebarProps {
  activeSection: string;
  setActiveSection: (section: string) => void;
  darkMode: boolean;
  dashboardStatus?: DashboardStatus | null;
}

// Items that require keywords to be locked before access
const GATED_ITEMS = new Set([
  'as-is-state', 'goal-setting', 'action-plan',
  'implementation-overview',
  'blog', 'faq', 'on-page-content', 'image-optimizer', 'meta-title-generator',
  'off-page-content', 'image-submission', 'classified-ads', 'pdf-submission', 'web2-submission',
  'governance-overview', 'as-is-performance', 'goals-tracking', 'action-plan-tracking', 'competitor-benchmark',
  'optimization-overview', 'ecosystem-monitor',
]);

export default function Sidebar({ activeSection, setActiveSection, darkMode, dashboardStatus }: SidebarProps) {
  const [expandedSection, setExpandedSection] = useState<string | null>('strategy');

  const sections = [
    {
      id: 'strategy',
      name: 'Strategy',
      icon: 'ri-lightbulb-line',
      mainRoute: 'strategy-dashboard',
      subsections: [
        { id: 'as-is-state', name: 'As-Is State', icon: 'ri-file-list-line' },
        { id: 'goal-setting', name: 'Goal Setting', icon: 'ri-flag-line' },
        { id: 'action-plan', name: 'Action Plan', icon: 'ri-task-line' }
      ]
    },
    {
      id: 'implementation',
      name: 'Implementation',
      icon: 'ri-tools-line',
      mainRoute: 'implementation-overview',
      subsections: [
        { id: 'on-page-content', name: 'On-Page Content', icon: 'ri-layout-top-line' },
        { id: 'off-page-content', name: 'Off-Page Content', icon: 'ri-external-link-line' }
      ]
    },
    {
      id: 'governance',
      name: 'Governance',
      icon: 'ri-dashboard-line',
      mainRoute: 'governance-overview',
      subsections: [
        { id: 'as-is-performance', name: 'As-Is Performance', icon: 'ri-line-chart-line' },
        { id: 'goals-tracking', name: 'Goal Tracking', icon: 'ri-flag-line' },
        { id: 'action-plan-tracking', name: 'Action Plan Tracking', icon: 'ri-task-line' },
        { id: 'competitor-benchmark', name: 'Competitor Benchmark', icon: 'ri-bar-chart-box-line' }
      ]
    },
    {
      id: 'optimization',
      name: 'Optimization',
      icon: 'ri-rocket-line',
      mainRoute: 'optimization-overview',
      subsections: [
        { id: 'ecosystem-monitor', name: 'Ecosystem Monitor', icon: 'ri-radar-line' }
      ]
    }
  ];


  const toggleSection = (sectionId: string, mainRoute?: string) => {
    if (mainRoute) {
      setActiveSection(mainRoute);
    }
    setExpandedSection(expandedSection === sectionId ? '' : sectionId);
  };

  return (
    <div className={`w-64 ${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'} border-r h-screen flex flex-col`}>
      {/* Logo and Brand */}
      <div className="p-2 border-b border-gray-200 dark:border-gray-700 flex justify-center">
        <div className="flex items-center justify-center overflow-hidden flex-shrink-0 w-full px-2">
          <img src={logo} alt="Marketing4Sight" className="h-16 w-auto object-contain" />
        </div>
      </div>



      {/* Navigation Menu */}
      <nav className="flex-1 p-4 overflow-y-auto">
        <div className="space-y-2">
          {sections.map((section) => {
            return (
              <div key={section.id}>
                {/* Section Header */}
                <button
                  onClick={() => toggleSection(section.id, section.mainRoute || undefined)}
                  className={`w-full flex items-center justify-between p-3 rounded-lg transition-colors ${
                    expandedSection === section.id
                      ? darkMode ? 'bg-gray-700 text-white' : 'bg-gray-100 text-gray-900'
                      : darkMode ? 'text-gray-400 hover:bg-gray-700 hover:text-white' : 'text-gray-600 hover:bg-gray-100'
                  }`}
                >
                  <div className="flex items-center space-x-3">
                    <i className={`${section.icon} text-lg`}></i>
                    <span className="font-medium">{section.name}</span>
                  </div>
                  <i className={`ri-arrow-${expandedSection === section.id ? 'up' : 'down'}-s-line text-sm`}></i>
                </button>

                {/* Section Items */}
                {expandedSection === section.id && (
                <div className="ml-4 mt-2 space-y-1">
                  {section.subsections.map((item) => {
                    const isSoon = (item as any).comingSoon;
                    const isLocked = !isSoon && GATED_ITEMS.has(item.id) && dashboardStatus && !dashboardStatus.keywords_locked;
                    return isSoon ? (
                      <div
                        key={item.id}
                        className={`w-full flex items-center justify-between p-2 rounded-lg cursor-not-allowed opacity-60 ${darkMode ? 'text-gray-500' : 'text-gray-400'
                          }`}
                      >
                        <div className="flex items-center space-x-3">
                          {item.icon && <i className={`${item.icon} text-sm`}></i>}
                          <span className="text-sm">{item.name}</span>
                        </div>
                        <span className="px-1.5 py-0.5 bg-orange-100 text-orange-600 text-xs font-semibold rounded-full whitespace-nowrap">
                          Soon
                        </span>
                      </div>
                    ) : (
                      <button
                        key={item.id}
                        onClick={() => setActiveSection(item.id)}
                        className={`w-full flex items-center justify-between p-2 rounded-lg text-left transition-colors ${isLocked
                          ? `opacity-60 ${darkMode ? 'text-gray-500 hover:bg-gray-700' : 'text-gray-400 hover:bg-gray-100'}`
                          : activeSection === item.id
                            ? 'bg-blue-600 text-white'
                            : darkMode
                              ? 'text-gray-400 hover:bg-gray-700 hover:text-white'
                              : 'text-gray-600 hover:bg-gray-100'
                          }`}
                      >
                        <div className="flex items-center space-x-3">
                          {item.icon && <i className={`${item.icon} text-sm`}></i>}
                          <span className="text-sm">{item.name}</span>
                        </div>
                        {isLocked && (
                          <span className="px-1.5 py-0.5 bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400 text-xs font-semibold rounded-full whitespace-nowrap">
                            <i className="ri-lock-line"></i>
                          </span>
                        )}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
        </div>
      </nav>

      {/* DIY Help Section */}
      <div className="p-4 border-t border-gray-200 dark:border-gray-700">
        <div className={`p-3 rounded-lg ${darkMode ? 'bg-gray-700' : 'bg-blue-50'}`}>
          <div className="flex items-center space-x-2 mb-2">
            <i className="ri-question-line text-blue-600"></i>
            <span className={`text-sm font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>
              DIY Guide
            </span>
          </div>
          <p className="text-xs text-gray-600 dark:text-gray-400 mb-2">
            Need help? Click any info icon for explanations.
          </p>
        </div>
      </div>
    </div>
  );
}
